import {
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useParams } from '@tanstack/react-router';
import { isNil } from 'lodash-es';
import { lruMemoize } from 'reselect';

import type {
  AllWordsIndex,
  SpeakerWordIds,
  Word,
} from '@iconik-io/shared-lib/src/react/components/panels/transcriptions_panel/types';
import { rangeToHmsString } from '@iconik-io/shared-lib/src/utils/range_to_hms_string';

import TooltipTrigger from '../../../components/tooltip_trigger/tooltip_trigger';
import { TriggerMenu } from '../../../components/trigger_menu/trigger_menu';
import {
  type UnifiedPlayerStore,
  withPlayerContext,
} from '../../../components/unified_player/with_player_context';
import {
  TRANSCRIPTIONS_CHANGE_WORDS_SPEAKER,
  TRANSCRIPTIONS_EDIT_SPEAKERS,
} from '../../../constants/modals';
import { selectSelectedTranscriptionPropertiesIdByAssetId } from '../../../ducks/asset_transcriptions/selectors';
import { openModal } from '../../../ducks/modals_manager/slice';
import {
  selectAsset,
  selectAssetProxyTimecodeOptions,
} from '../../../ducks/pages/asset/selectors';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import IconButton from '../../../ui/icon_button/icon_button';
import type { MenuItem } from '../../../ui/menus/menu/types';
import { targetElementOf } from '../../../utils/event_target';
import { selectUserRoles } from '../../../utils/get_user_roles';
import { waitForElement } from '../../../utils/wait_for_element';
import {
  getSpeakerPartBySegmentIdMap,
  selectAllWords,
  selectSpeakerMenuItems,
  selectTranscriptionPartsAndSpeakerLabels,
} from '../selectors';
import WordEditor from '../single_word_edit/single_word_edit';
import { Words } from './words';

import styles from './speaker_mode.module.css';

type Props = {
  markedWordIds: string[];
  currentMatchWordId?: string;
} & UnifiedPlayerStore;

const getWordId = (word: Word) => `w_${word.segmentId}_${word.localIndex}`;

const getHighlightedWordIds = lruMemoize(
  (allWordsIndex: AllWordsIndex, allWords: Word[], currentWord?: Word) => {
    const highlightedTextWordIds: string[] = [];
    if (currentWord) {
      const currentWordId = getWordId(currentWord);
      const currentWordIndex = allWordsIndex[currentWordId];
      for (const word of allWords) {
        const wordId = getWordId(word);
        if (allWordsIndex[wordId] < currentWordIndex) {
          highlightedTextWordIds.push(wordId);
        } else {
          break;
        }
      }
    }
    return highlightedTextWordIds;
  },
);

type SpeakerPart = {
  speakerIndex: number | undefined;
  words: Word[];
};

function TranscriptionsSpeakerMode({
  markedWordIds,
  currentMatchWordId,
  playerStore,
}: Props): ReactNode {
  const dispatch = useAppDispatch();
  const { id: assetId, versionId } = useParams({ strict: false });

  const [currentWord, setCurrentWord] = useState<Word | undefined>();
  const [selectedWordIds, setSelectedWordIds] = useState<string[]>([]);
  const anchorElementRef = useRef<HTMLElement | null>(null);

  const { speakerParts, speakerLabels } = useAppSelector((state) =>
    selectTranscriptionPartsAndSpeakerLabels(state, assetId),
  );
  const { allWords, allWordsIndex } = useAppSelector((state) =>
    selectAllWords(state, assetId),
  );
  const segments = useAppSelector((state) => state.entities.segments);
  const timecodeOptions = useAppSelector((state) =>
    selectAssetProxyTimecodeOptions(state, assetId, versionId),
  );
  const transcriptionProperties = useAppSelector((state) =>
    selectSelectedTranscriptionPropertiesIdByAssetId(state, assetId ?? ''),
  );
  const isSubclip = useAppSelector(
    (state) => selectAsset(state, assetId)?.type === 'SUBCLIP',
  );
  const userRoles = useAppSelector(selectUserRoles);

  // The document-level listeners below are registered once, exactly as the
  // class version was, so they must not close over a stale render. Everything
  // they read goes through this ref instead.
  const latest = useRef({
    allWords,
    allWordsIndex,
    speakerParts,
    speakerLabels,
    timecodeOptions,
    selectedWordIds,
  });
  latest.current = {
    allWords,
    allWordsIndex,
    speakerParts,
    speakerLabels,
    timecodeOptions,
    selectedWordIds,
  };

  const clearSelectedWords = useCallback(() => {
    window.getSelection()?.removeAllRanges();
    setSelectedWordIds([]);
  }, []);

  const getFirstAndLastSelectedWord = useCallback(() => {
    const {
      allWords: words,
      allWordsIndex: index,
      selectedWordIds: selection,
    } = latest.current;
    const firstWordIndex = index[selection[0]];
    const firstWord = words[firstWordIndex];
    const lastWordIndex = index[selection[selection.length - 1]];
    const lastWord = words[lastWordIndex];
    return { firstWord, firstWordIndex, lastWord, lastWordIndex };
  }, []);

  const handleSelectionChange = useCallback(() => {
    const { allWords: words, allWordsIndex: index } = latest.current;
    const selection = window.getSelection();
    if (!selection) {
      clearSelectedWords();
      return;
    }

    const { focusNode } = selection;

    if (!anchorElementRef.current || !focusNode?.parentElement) {
      clearSelectedWords();
      return;
    }

    const nextSelectedWordIds = [];
    const firstWordIndex = index[anchorElementRef.current.id];
    const lastWordIndex = index[focusNode.parentElement.id];

    if (firstWordIndex === undefined || lastWordIndex === undefined) {
      return;
    }
    if (firstWordIndex < 0 || lastWordIndex < 0) {
      return;
    }

    if (firstWordIndex < lastWordIndex) {
      for (let i = firstWordIndex; i <= lastWordIndex; i += 1) {
        const word = words[i];
        if (word) {
          nextSelectedWordIds.push(getWordId(word));
        }
      }
    } else {
      for (let i = lastWordIndex; i <= firstWordIndex; i += 1) {
        const word = words[i];
        nextSelectedWordIds.push(getWordId(word));
      }
    }

    setSelectedWordIds(nextSelectedWordIds);
  }, [clearSelectedWords]);

  useEffect(() => {
    const handleTimeUpdate = (event: Event) => {
      const video = targetElementOf(event.target, HTMLMediaElement);
      if (!video) {
        return;
      }
      const timeMs = Math.floor(video.currentTime * 1000);
      const { allWords: words } = latest.current;
      for (let i = 0; i < words.length; i += 1) {
        const word = words[i];
        if (word.timeEnd && word.timeEnd >= timeMs) {
          setCurrentWord(word);
          break;
        }
      }
    };

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (latest.current.selectedWordIds.length && event.code === 'Escape') {
        clearSelectedWords();
      }
    };

    const handleCopy = (event: ClipboardEvent) => {
      if (!event.clipboardData || latest.current.selectedWordIds.length === 0) {
        return;
      }

      const {
        allWords: words,
        speakerLabels: labels,
        timecodeOptions: timecodes,
        speakerParts: parts,
      } = latest.current;
      const { firstWord, firstWordIndex, lastWord, lastWordIndex } =
        getFirstAndLastSelectedWord();

      if (!firstWord || !lastWord) {
        event.clipboardData.setData('text/plain', '');
        event.preventDefault();
        return;
      }

      const speakersMap = getSpeakerPartBySegmentIdMap(parts);

      const copiedSpeakerParts: SpeakerPart[] = [];
      let prevSpeakerIndex: number | undefined;
      let currentSpeakerPart: SpeakerPart | undefined;
      for (
        let wordIndex = firstWordIndex;
        wordIndex <= lastWordIndex;
        wordIndex += 1
      ) {
        const word = words[wordIndex];
        const speakerIndex = speakersMap.get(word.segmentId);
        if (speakerIndex !== prevSpeakerIndex) {
          currentSpeakerPart = { speakerIndex, words: [] };
          copiedSpeakerParts.push(currentSpeakerPart);
        }
        if (currentSpeakerPart) {
          currentSpeakerPart.words.push(word);
        }
        prevSpeakerIndex = speakerIndex;
      }

      const textParts = copiedSpeakerParts.map((part) => {
        let speakerHeader = '';

        speakerHeader += !isNil(part.speakerIndex)
          ? labels[part.speakerIndex]
          : 'Speaker not set';

        if (timecodes) {
          const time = rangeToHmsString({
            startMilliseconds: part.words.at(0)?.timeStart,
            endMilliseconds: part.words.at(-1)?.timeEnd,
            timecodeOptions: timecodes,
          });
          if (time) {
            speakerHeader += ` (${time})`;
          }
        }

        return `${speakerHeader}\n\n${part.words
          .map((word) => word.value)
          .join(' ')}`;
      });

      event.clipboardData.setData('text/plain', textParts.join('\n\n\n'));
      event.preventDefault();
    };

    const mediaElement = document.querySelector('video, audio');
    const assetOverview = document.querySelector<HTMLElement>('#asset_content');
    if (mediaElement) {
      mediaElement.addEventListener('timeupdate', handleTimeUpdate);
    } else if (assetOverview) {
      waitForElement('video, audio', assetOverview).then((element) => {
        element.addEventListener('timeupdate', handleTimeUpdate);
      });
    }

    document.addEventListener('keydown', handleDocumentKeyDown);
    document.addEventListener('copy', handleCopy);

    return () => {
      const media = document.querySelector('video, audio');
      if (media) {
        media.removeEventListener('timeupdate', handleTimeUpdate);
      }
      document.removeEventListener('keydown', handleDocumentKeyDown);
      document.removeEventListener('copy', handleCopy);
    };
  }, [clearSelectedWords, getFirstAndLastSelectedWord]);

  // Words can disappear when the transcription reloads; drop any selected id
  // that no longer resolves.
  useEffect(() => {
    setSelectedWordIds((previous) => {
      const updated = previous.filter(
        (wordId) => allWordsIndex[wordId] !== undefined,
      );
      return updated.length !== previous.length ? updated : previous;
    });
  }, [allWordsIndex]);

  const handleMouseDown: MouseEventHandler<HTMLDivElement> = (event) => {
    const element = targetElementOf(event.target, HTMLElement);
    const wordId = element?.id;
    if (wordId && allWordsIndex[wordId] !== undefined) {
      anchorElementRef.current = element;
      const wordIndex = allWordsIndex[wordId];
      if (allWords[wordIndex]) {
        playerStore.setTargetTime((allWords[wordIndex].timeStart ?? 0) / 1000);
      }

      setSelectedWordIds([wordId]);

      document.addEventListener('selectionchange', handleSelectionChange);
    } else {
      // Click outside the words area clears range selection
      playerStore.segment.clearCurrentSegment();
      clearSelectedWords();
    }
  };

  const handleMouseUp: MouseEventHandler<HTMLDivElement> = () => {
    if (selectedWordIds.length > 1) {
      const { firstWord, lastWord } = getFirstAndLastSelectedWord();
      if (firstWord && lastWord) {
        const timeStart = firstWord.timeStart;
        const timeEnd = lastWord.timeEnd;
        if (timeStart !== undefined && timeEnd !== undefined) {
          playerStore.segment.setCurrentSegmentRange({
            start: timeStart / 1000,
            end: timeEnd / 1000,
          });
        }
      }
    }
    document.removeEventListener('selectionchange', handleSelectionChange);
  };

  const handleChangeSpeaker = (menuItem: MenuItem, wordIds: string[]) => {
    if (menuItem.name === 'manage') {
      dispatch(openModal({ id: TRANSCRIPTIONS_EDIT_SPEAKERS }));
    } else if (menuItem.name) {
      const [, speakerId] = menuItem.name.split('_');
      if (speakerId) {
        const speakerWordIds: SpeakerWordIds = {
          id: speakerId,
          wordIds,
        };
        dispatch(
          openModal({
            id: TRANSCRIPTIONS_CHANGE_WORDS_SPEAKER,
            contextItems: [speakerWordIds],
          }),
        );
      }
    }
  };

  const speakersMap = getSpeakerPartBySegmentIdMap(speakerParts);
  const selectedSpeakerIndex = currentWord?.segmentId
    ? speakersMap.get(currentWord?.segmentId)
    : undefined;
  const selectedSpeakerLabel = selectedSpeakerIndex
    ? speakerLabels[selectedSpeakerIndex]
    : '';

  const highlightedTextWordIds = getHighlightedWordIds(
    allWordsIndex,
    allWords,
    currentWord,
  );
  const filteredSelection = selectedWordIds.filter(
    (wordId) => allWordsIndex[wordId] !== undefined,
  );
  const singleSelection =
    filteredSelection.length === 1
      ? allWords[allWordsIndex[filteredSelection[0]]]
      : undefined;

  return (
    <>
      <div
        className={styles.root}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <Words
          canEdit={userRoles.canWriteTranscriptions && !isSubclip}
          speakerParts={speakerParts}
          speakerLabels={speakerLabels}
          segments={segments}
          openModal={(payload) => dispatch(openModal(payload))}
          selectedWordIds={filteredSelection}
          highlightedTextWordIds={highlightedTextWordIds}
          markedWordIds={markedWordIds}
          currentMatchWordId={currentMatchWordId}
          transcriptionProperties={transcriptionProperties}
        />
      </div>
      {selectedWordIds.length &&
      !isNil(selectedSpeakerIndex) &&
      userRoles.canWriteTranscriptions ? (
        <div className={styles.action_box}>
          {isSubclip ? (
            <TooltipTrigger tooltipText={selectedSpeakerLabel}>
              <IconButton
                iconName="user_user"
                iconSizeUAD="m"
                className={styles.change_words_speaker}
              />
            </TooltipTrigger>
          ) : (
            <TriggerMenu
              triggerElement={
                <IconButton
                  iconName="user_user"
                  iconSizeUAD="m"
                  className={styles.change_words_speaker}
                />
              }
              menuItems={selectSpeakerMenuItems(
                speakerLabels,
                selectedSpeakerIndex,
              )}
              onMenuItemClick={(menuItem) =>
                handleChangeSpeaker(menuItem, selectedWordIds)
              }
              showIcons
            />
          )}
          {singleSelection ? (
            <WordEditor word={singleSelection} />
          ) : (
            <div className={styles.multiwords}>
              <strong>{selectedWordIds.length} words selected</strong>
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}

export default withPlayerContext(TranscriptionsSpeakerMode);
